import { defineStore } from "pinia";
import { validate as validateUuid } from "uuid";
import Api from "src/utils/api";
import Constants from "src/utils/constants";
import Helper from "src/utils/helper";
import Exception from "src/utils/exception";
import {
  findUnresolvedTokens,
  isDraftEmailId,
  templateHasNoRecipients,
  validateEmailTemplate
} from "src/utils/emailTemplate/draftEmails";
import { renderTemplateMessage } from "src/utils/emailTemplate/deliveryGuard";
import { formatEmailBody } from "src/utils/emailTemplate/bodyFormat";

/**
 * Where a template's variables come from, or null when it has none.
 */
function sourceFromTemplate(template) {
  const inner = template?.template;
  return inner?.source?.type ? inner.source : null;
}

/**
 * The field tokens in a message — `$(uuid)` only.
 *
 * Deliberately not every `$(…)`: the well-known response keys
 * (`submission_status`, `submiited_by_name`, `submit_acted_at_format`) survive
 * a change of source, so counting them would make a "these will stop resolving"
 * warning fire when nothing is wrong. A uuid token is always field-specific and
 * always breaks.
 */
function fieldTokens(text) {
  if (typeof text !== "string") return [];
  return [...text.matchAll(Constants.constants.EMAIL_TOKEN_PATTERN)]
    .filter(([, key]) => validateUuid(key))
    .map(([token]) => token);
}

export const useEmailTemplatesStore = defineStore("email_templates_store", {
  state: () => ({
    templateList: [],
    totalEmails: 0,
    template: null,
    original: null, // last-persisted snapshot, for isDirty
    isNew: false,
    loading: false,

    // Variable keys the palette's active source offers, or null when there is
    // nothing to compare against. Drives `unmatchedTokens`.
    sourceVariableKeys: null,

    // Set by the send configs editor, which keeps unfinished configs to itself
    // and only hands valid ones to `template.send_configs`. Blocks the save.
    hasIncompleteSendConfigs: false
  }),

  getters: {
    /**
     * Serialised comparison — the editable surface is small and all JSON.
     * Needs no null guard: with nothing loaded both sides stringify to "null"
     * and compare equal.
     */
    isDirty: (state) =>
      JSON.stringify(state.template) !== JSON.stringify(state.original),

    /**
     * The template's variable source, or null when it has none.
     */
    source: (state) => sourceFromTemplate(state.template),

    /**
     * Field-specific tokens across subject and body. Used to warn that a change
     * of source leaves them unresolvable; see `fieldTokens` for why only
     * uuid-shaped ones count.
     */
    messageFieldTokens: (state) => [
      ...fieldTokens(state.template?.template?.subject),
      ...fieldTokens(state.template?.template?.body)
    ],

    hasNoRecipients: (state) => templateHasNoRecipients(state.template),

    /** Configs match on the template's variables, so without any there's nothing to configure. */
    canAddSendConfig: (state) => (state.sourceVariableKeys ?? []).length > 0,

    /**
     * The shared title / subject / body rules, plus one builder-only rule: a
     * template that was loaded with a variable source cannot be saved without
     * one. Switching to a tab that hasn't been filled in leaves no source, and
     * saving then would silently drop the one the template came in with.
     */
    validationErrors: (state) => {
      const errors = validateEmailTemplate(state.template);
      const originalType = sourceFromTemplate(state.original)?.type;
      if (
        state.template &&
        originalType &&
        !sourceFromTemplate(state.template)
      ) {
        const labels = {
          [Constants.enums.EMAIL_SOURCE_TYPES.FORM]: ["a form", "A form"],
          [Constants.enums.EMAIL_SOURCE_TYPES.JSON]: ["an object", "An object"],
          [Constants.enums.EMAIL_SOURCE_TYPES.TABLE]: ["a table", "A table"]
        };
        const [phrase, tab] = labels[originalType] ?? [
          "a variable source",
          "it"
        ];
        errors.push({
          field: "source",
          message: `This template was built from ${phrase}. Finish choosing a source on the current tab, or switch back to “${tab}”, before saving.`
        });
      }

      if (state.template?.send_configs?.length && !state.canAddSendConfig) {
        errors.push({
          field: "send_configs",
          message:
            "This template has no variables, so it can't have send configs. Remove them or choose a variable source."
        });
      }
      if (state.hasIncompleteSendConfigs) {
        errors.push({
          field: "send_configs",
          message: "Finish or remove incomplete send configs before saving."
        });
      }
      return errors;
    },

    /**
     * Every `$(…)` token in the subject or body whose key the active source
     * doesn't offer — each one, including send-time keys, so the author can
     * decide what will exist at runtime. Empty when there is nothing to
     * compare against (`sourceVariableKeys` is null).
     */
    unmatchedTokens: (state) => {
      if (!Array.isArray(state.sourceVariableKeys)) return [];
      const keys = new Set(state.sourceVariableKeys);
      return findUnresolvedTokens(state.template, (key) => keys.has(key));
    },

    canSave() {
      return (
        !this.loading && this.isDirty && this.validationErrors.length === 0
      );
    }
  },

  actions: {
    async loadTemplate(id) {
      this.loading = true;
      try {
        const res = await Api.EmailTemplates.getEmailTemplateById(id, false);
        if (res?.error) throw new Error(res.error);
        if (!res?.data) throw new Error("Email template not found");

        const row = Helper.deepCopy(res.data);
        if (row.template) {
          row.template.body = await formatEmailBody(row.template.body);
        }

        const configsRes =
          await Api.EmailTemplates.getEmailTemplateSendConfigs(id);
        if (configsRes?.error) throw new Error(configsRes.error);
        row.send_configs = (configsRes?.data ?? []).map((config) => ({
          id: config.id,
          conditions: config.conditions ?? [],
          operator: config.operator ?? null,
          sending_data: config.sending_data ?? { to: [], cc: [] }
        }));

        this.template = row;
        this.original = Helper.deepCopy(row);
        this.isNew = false;
        this.hasIncompleteSendConfigs = false;
        return this.template;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Sets where the template's variables come from.
     *
     * The whole source object is replaced, never merged: a template draws from
     * exactly one of `form`, `json` or `table`, so switching type must not
     * leave the previous type's keys behind — a stale `form_template_id` under
     * `type: "json"` would be a second answer to a question that has one.
     * Pass null to clear the source entirely.
     */
    setSource(source) {
      if (!this.template) return;
      const inner = { ...(this.template.template ?? {}) };
      if (source?.type) {
        inner.source = source;
      } else {
        delete inner.source;
      }
      this.template = { ...this.template, template: inner };
    },

    /**
     * Called by the send configs editor whenever its list changes: `configs`
     * is only the valid ones, and `hasIncomplete` says whether any it is
     * holding back are part-filled.
     */
    setSendConfigs(configs = [], hasIncomplete = false) {
      if (!this.template) return;
      this.template = {
        ...this.template,
        send_configs: Helper.deepCopy(configs)
      };
      this.hasIncompleteSendConfigs = hasIncomplete;
    },

    /**
     * The palette's source change, from whichever tab it's on. Variables are
     * specific to the source they came from — field ids belong to one form,
     * keys to one pasted object — so tokens written against the previous one
     * stop resolving and send as blank. Said once, after the fact, and not on
     * the first pick, when there is nothing to invalidate yet.
     */
    changeSource(source) {
      const hadSource = Boolean(this.source);
      const changed =
        JSON.stringify(this.source ?? null) !== JSON.stringify(source ?? null);
      const tokenCount = this.messageFieldTokens.length;

      this.setSource(source);

      if (hadSource && source && changed && tokenCount) {
        Helper.showToast(
          "Variables no longer match",
          `${tokenCount} variable(s) in this email came from the previous source and will send as blank. Replace them from the list.`,
          "warn",
          8000
        );
      }
    },

    /** Records the active source's variable keys, or null for "nothing to compare". */
    setSourceVariableKeys(keys) {
      this.sourceVariableKeys = Array.isArray(keys) ? [...keys] : null;
    },

    /**
     * Opens a blank template in the builder. Saving it runs the create RPC.
     */
    newTemplate(draft = null) {
      const blank = {
        id: null,
        name: draft?.name ?? "",
        description: draft?.description ?? "",
        template: Helper.deepCopy(draft?.template ?? { subject: "", body: "" }),
        default_sending_data: Helper.deepCopy(
          draft?.default_sending_data ?? {
            to: [],
            cc: [],
            from: Constants.enums.EMAIL_RECIPIENT_TYPES.NO_REPLY_APP
          }
        ),
        send_configs: Helper.deepCopy(draft?.send_configs ?? [])
      };
      this.template = blank;
      this.original = Helper.deepCopy(blank);
      this.isNew = true;
      this.loading = false;
      this.hasIncompleteSendConfigs = false;
      return this.template;
    },

    toDraft() {
      if (!this.template) return null;
      this.trimEditableText();
      return Helper.deepCopy({
        name: this.template.name,
        description: this.template.description,
        template: this.template.template,
        default_sending_data: this.template.default_sending_data,
        send_configs: this.template.send_configs ?? []
      });
    },

    /**
     * Shallow-merges a patch into the loaded template. `template` (the
     * { subject, body } jsonb) is merged one level deeper so a caller can
     * patch just the subject without clobbering the body.
     */
    updateTemplate(patch = {}) {
      if (!this.template) return;
      const { template: templatePatch, ...rest } = patch;
      this.template = {
        ...this.template,
        ...rest,
        ...(templatePatch
          ? {
              template: { ...(this.template.template ?? {}), ...templatePatch }
            }
          : {})
      };
    },

    /**
     * Persists the loaded template.
     *
     * One save, but two RPCs behind it, chosen by whether a row exists yet.
     * They are kept apart rather than merged into an upsert on purpose: a
     * single entry point would read a missing id as "create", so a dropped id
     * would quietly produce a duplicate template instead of an error.
     *
     * @returns {Promise<{template: Object, created: boolean}>}
     */
    async saveTemplate() {
      if (!this.template) throw new Error("No template loaded");

      this.loading = true;
      try {
        this.trimEditableText();
        const created = !this.template.id;
        const template = created
          ? await this.createTemplate()
          : await this.updateTemplateRow();
        return { template, created };
      } finally {
        this.loading = false;
      }
    },

    /**
     * Trims the free-text fields in place, immediately before a save.
     *
     * The write path trims on the way past regardless — the RPCs `btrim` both
     * names, and `blankToNull` trims the descriptions — so without this the
     * builder would keep showing `" Name "` while the database held `"Name"`,
     * and the baseline taken after the save would bake that difference in.
     * Normalising here means state, baseline and row all agree.
     */
    trimEditableText() {
      if (!this.template) return;
      // Only strings are touched: a null description stays null, which is what
      // the column holds for "no description" and what blankToNull sends.
      const trim = (value) =>
        typeof value === "string" ? value.trim() : value;
      this.template = {
        ...this.template,
        name: trim(this.template.name),
        description: trim(this.template.description)
      };
    },

    /**
     * Creates the template, then adopts the id so the builder carries on
     * editing the real row without needing a reload.
     */
    async createTemplate() {
      const res =
        await Api.EmailTemplates.createEmailTemplateWithDistributionGroup({
          name: this.template.name,
          template: this.template.template,
          description: this.template.description,
          source: this.source,
          userId: Helper.getCurrentUserId(),
          defaultSendingData: this.template.default_sending_data,
          sendConfigs: this.template.send_configs ?? []
        });
      if (res?.error) throw new Error(res.error);

      this.template = {
        ...this.template,
        id: res.data.email_template_id
      };
      this.original = Helper.deepCopy(this.template);
      this.isNew = false;
      return this.template;
    },

    /** Updates the existing row. */
    async updateTemplateRow() {
      const res =
        await Api.EmailTemplates.updateEmailTemplateWithDistributionGroup({
          id: this.template.id,
          name: this.template.name,
          template: this.template.template,
          description: this.template.description,
          source: this.source,
          userId: Helper.getCurrentUserId(),
          defaultSendingData: this.template.default_sending_data,
          sendConfigs: this.template.send_configs ?? []
        });
      if (res?.error) throw new Error(res.error);

      // Re-baseline from what we sent rather than the response: the RPC returns
      // ids, not the row, and isDirty only needs the editable surface to match.
      this.original = Helper.deepCopy(this.template);
      return this.template;
    },

    /**
     * Soft-deletes a template. Its distribution groups are shared and are
     * left alone.
     *
     * Clears the builder when the deleted row is the one loaded, so nothing is
     * left editing a template that no longer exists.
     *
     * @returns {Promise<{email_template_id: string}|null>}
     */
    async deleteTemplate(id) {
      if (!id) throw new Error("Email template ID is required");

      const res =
        await Api.EmailTemplates.deleteEmailTemplateWithDistributionGroup({
          id,
          userId: Helper.getCurrentUserId()
        });
      if (res?.error) throw new Error(res.error);

      if (this.template?.id === id) this.resetBuilder();
      return res.data ?? null;
    },

    /** Clears the builder surface. Leaves the dashboard list state alone. */
    resetBuilder() {
      this.template = null;
      this.original = null;
      this.isNew = false;
      this.loading = false;
      this.sourceVariableKeys = null;
      this.hasIncompleteSendConfigs = false;
    },

    async getEmailTemplateById(id) {
      const response = await Api.EmailTemplates.getEmailTemplateById(id, false);
      return response;
    },

    async getEmailTemplates(config) {
      let _res = await Api.EmailTemplates.getEmailTemplates(config);
      this.totalEmails = _res.totalItems || 0;
      this.templateList = _res.data || [];
      return _res;
    },

    /** Base tables in `public`, for the builder's table variable source. */
    async listSourceTables() {
      return await Api.SchemaBrowser.listTables();
    },

    /** One table's columns, for the builder's table variable source. */
    async listSourceTableColumns(tableName) {
      return await Api.SchemaBrowser.listTableColumns(tableName);
    },

    /**
     * Renders the template against `data` and sends it through send-email-v2,
     * which decides the recipients: the template's default_sending_data plus
     * every send config whose conditions match `data`.
     */
    async sendEmailFromTemplate(templateId, attachments = [], data = {}) {
      try {
        if (isDraftEmailId(templateId)) {
          console.warn(
            "[sendEmailFromTemplate] skipped unpublished drafted email",
            templateId
          );
          return { error: "This email has not been published yet" };
        }
        const emailTemplate = await this.getEmailTemplateById(templateId);

        const { subject, body } = await renderTemplateMessage(
          emailTemplate.data,
          data
        );

        const response = await Api.EmailTemplates.sendTemplatedEmail({
          emailTemplateId: templateId,
          data,
          roleRecipients: await Helper.resolveRoleRecipients(),
          subject,
          body,
          attachments
        });
        return response;
      } catch (error) {
        Exception.catchException(error);
        return { error: error.message };
      }
    }
  }
});
